import { Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useWhiteboardStore, WhiteboardElement } from '../../../store/whiteboardStore';
import { Badge } from '../../../components/Badge';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';

export function Whiteboard() {
  const { elements, addElement, deleteElement, updateElement, loadBoard, saveNow, isLoading, isSaving, boardName } = useWhiteboardStore();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tool, setTool] = useState<WhiteboardElement['type']>('sticky');
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    loadBoard();
  }, []);

  const handleCanvasClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).id === 'whiteboard-canvas') {
      setSelectedId(null);
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const x = (e.clientX - rect.left) / zoom;
      const y = (e.clientY - rect.top) / zoom;

      addElement({
        type: tool,
        x,
        y,
        width: tool === 'sticky' ? 200 : 150,
        height: tool === 'sticky' ? 200 : 100,
        text: tool === 'sticky' ? 'Double click to edit' : '',
        color: tool === 'sticky' ? '#facc15' : '#3b82f6',
        owner: 'Current User',
      });
    }
  };

  return (
    <div className="relative w-full h-[calc(100vh-180px)] overflow-hidden bg-background rounded-lg border border-border shadow-2xl ring-1 ring-white/5">
      {/* Toolbar */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 p-2 bg-card/80 border border-border rounded-lg shadow-2xl">
        {[
          { id: 'sticky' as const, icon: '', label: 'Sticky' },
          { id: 'rect' as const, icon: '', label: 'Box' },
          { id: 'circle' as const, icon: '', label: 'Circle' },
          { id: 'text' as const, icon: 'T', label: 'Text' },
        ].map(item => (
          <Button variant="unstyled" size="unstyled"
            key={item.id}
            onClick={() => setTool(item.id)}
            className={`w-10 h-10 flex flex-col items-center justify-center rounded-lg transition-all ${tool === item.id ? 'bg-primary text-white scale-110 shadow-lg shadow-primary/30' : 'text-muted-foreground hover:bg-card hover:text-foreground'
              }`}
            title={item.label}
          >
            <span className="text-lg">{item.icon}</span>
          </Button>
        ))}
        <div className="w-px h-6 bg-border-12 mx-2" />
        <Button variant="unstyled" size="unstyled" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} className="w-8 h-8 rounded-lg hover:bg-card text-muted-foreground font-semibold">-</Button>
        <span className="text-xs font-semibold text-foreground w-10 text-center">{Math.round(zoom * 100)}%</span>
        <Button variant="unstyled" size="unstyled" onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="w-8 h-8 rounded-lg hover:bg-card text-muted-foreground font-semibold">+</Button>
      </div>

      {/* Board name + auto-save status */}
      <div className="absolute top-6 right-6 z-20 flex items-center gap-2 px-3 py-2 bg-card/50 border border-border rounded-lg">
        <span className="text-xs font-semibold text-foreground">{boardName || 'Whiteboard'}</span>
        <Badge variant={isSaving ? 'warning' : 'success'} className="text-xs scale-90">
          {isSaving ? 'Saving…' : 'Saved'}
        </Badge>
      </div>

      {/* Canvas Area */}
      <div
        id="whiteboard-canvas"
        onClick={handleCanvasClick}
        className="w-full h-full relative cursor-crosshair overflow-auto pattern-grid"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}
      >
        <div
          className="absolute top-0 left-0 origin-top-left transition-transform duration-200"
          style={{ transform: `scale(${zoom})` }}
        >
          {elements.map(el => (
            <div
              key={el.id}
              onMouseDown={(e) => {
                e.stopPropagation();
                setSelectedId(el.id);
              }}
              className={`absolute select-none group transition-all ${selectedId === el.id ? 'ring-2 ring-primary ring-offset-4 ring-offset-bg-950' : ''
                }`}
              style={{
                left: el.x,
                top: el.y,
                width: el.width,
                height: el.height,
              }}
            >
              {el.type === 'sticky' && (
                <div
                  className="w-full h-full p-6 shadow-2xl rounded-sm transform rotate-1 flex flex-col justify-between"
                  style={{ backgroundColor: el.color }}
                >
                  <textarea
                    className="w-full h-full bg-transparent border-none resize-none text-bg-950 font-medium focus:outline-none placeholder-bg-950/30"
                    defaultValue={el.text}
                    onBlur={(e) => updateElement(el.id, { text: e.target.value })}
                    placeholder="Type here..."
                  />
                  <div className="flex justify-between items-center mt-2 opacity-50">
                    <span className="text-xs font-semibold text-bg-950">{el.owner}</span>
                    <Button variant="unstyled" size="unstyled" onClick={() => deleteElement(el.id)} className="text-xs hover:scale-125 transition-transform"><Trash2 className="size-3.5" /></Button>
                  </div>
                </div>
              )}

              {el.type === 'rect' && (
                <div
                  className="w-full h-full border-2 border-primary bg-primary/10 rounded-lg relative"
                  style={{ borderColor: el.color }}
                >
                  <Button variant="unstyled" size="unstyled" onClick={() => deleteElement(el.id)} className="absolute -top-3 -right-3 w-6 h-6 bg-destructive rounded-full text-white text-xs hidden group-hover:flex items-center justify-center shadow-lg"><X className="size-3.5" /></Button>
                </div>
              )}

              {el.type === 'circle' && (
                <div
                  className="w-full h-full border-2 border-success bg-success/10 rounded-full relative"
                  style={{ borderColor: el.color }}
                >
                  <Button variant="unstyled" size="unstyled" onClick={() => deleteElement(el.id)} className="absolute -top-1 -right-1 w-6 h-6 bg-destructive rounded-full text-white text-xs hidden group-hover:flex items-center justify-center shadow-lg"><X className="size-3.5" /></Button>
                </div>
              )}

              {el.type === 'text' && (
                <Input
                  className="bg-transparent border-none text-2xl font-semibold text-foreground placeholder-text-600 focus:outline-none min-w-[200px]"
                  defaultValue={el.text}
                  autoFocus
                  onBlur={(e) => updateElement(el.id, { text: e.target.value })}
                  placeholder="Click to type..."
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-6 left-6 z-20 flex gap-2">
        <Button variant="unstyled" size="unstyled"
          onClick={async () => { await saveNow(); toast.success('Whiteboard saved'); }}
          className="px-4 py-2 bg-background/80 border border-border rounded-lg text-xs font-semibold text-foreground hover:bg-primary/90 transition-colors"
        >
          Save Now
        </Button>
      </div>

      {isLoading && (
        <div className="absolute inset-0 z-50 bg-background/50 flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-xs font-semibold text-foreground">Syncing Canvas...</p>
        </div>
      )}
    </div>
  );
}
